import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MailCheckIcon } from 'lucide-react';
import { AuthError } from '@/components/auth/auth-error';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { messageForError } from '@/lib/api-client';
import { authApi } from '@/lib/api/auth';

const schema = z.object({ email: z.email('Enter a valid email address') });
type Values = z.infer<typeof schema>;

function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: Values) {
    setFormError(null);
    try {
      await authApi.forgotPassword(values);
      setSent(true);
    } catch (error) {
      setFormError(messageForError(error, 'We could not send that email. Try again shortly.'));
    }
  }

  const pending = form.formState.isSubmitting;

  if (sent) {
    return (
      <div className="flex flex-col gap-4">
        <span className="flex size-9 items-center justify-center rounded-md border border-hairline bg-success-soft text-success">
          <MailCheckIcon className="size-4" />
        </span>
        <h1 className="text-title font-semibold tracking-tight text-ink">Check your inbox</h1>
        <p className="text-data leading-relaxed text-ink-muted">
          If an account exists for that address, a reset link is on its way. The link expires in one
          hour.
        </p>
        <Link
          to="/login"
          className="text-data text-accent hover:underline underline-offset-4"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <span className="section-label">Account recovery</span>
        <h1 className="text-title font-semibold tracking-tight text-ink">Reset your password</h1>
        <p className="text-data text-ink-muted">
          We will email you a link to choose a new password.
        </p>
      </header>

      <Form {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <AuthError message={formError} />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Work email</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="email" disabled={pending} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" size="lg" disabled={pending} className="w-full">
            {pending ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      </Form>

      <Link
        to="/login"
        className="border-t border-hairline pt-4 text-data text-accent hover:underline underline-offset-4"
      >
        Back to sign in
      </Link>
    </div>
  );
}

export const Route = createFileRoute('/(auth)/forgot-password')({
  component: ForgotPasswordPage,
});
